import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Table, Form, Dropdown, ButtonGroup, Badge } from "react-bootstrap";
import Swal from "sweetalert2";
import db from "../../../../offline/db";
import { useAuth } from "../../../inicio/context/AuthContext";
import { useOnlineStatus } from "../../../inicio/hooks/useSync";
import { getPersonas, deletePersona, exportarExcelRBC, descargarPdfRBC, obtenerFichaHtmlRBC } from "../api/RBCAPI";
import { sincronizarDiscapacidadPendientes, eliminarOfflineDiscapacidad } from "../offline/discapacidadOffline";
import DiscapacidadModal from "../modal/DiscapacidadModal";
import ModalConsentimiento from "../modal/ModalConsentimiento";
import MapaDiscapacidad from "../components/MapaDiscapacidad";
import { ESTRATEGIA_COLOR } from "../../../dashboard/utils/colores";

const REGISTROS_POR_PAGINA = 10;
const INTERVALO_POLLING_MS = 30000; // "tiempo real" = refresco cada 30s mientras hay conexión

const AZUL = "#1a3a5c";
const TH = { padding: "11px 14px", fontWeight: 600, whiteSpace: "nowrap", fontSize: 12, border: "none" };
const TD = { padding: "10px 12px", verticalAlign: "middle", fontSize: 13 };
const BTN_ICONO = { background: "none", border: "none", cursor: "pointer", fontSize: 15, marginRight: 4 };

// Sequelize devuelve las asociaciones con nombres pluralizados
// impredecibles (CategoriaDiscapacidads, Dificultads...), no en minúscula
// como se guardan en el payload — mismo criterio que en el modal, el Excel
// y la ficha PDF.
function getAsoc(persona, ...posiblesNombres) {
  for (const nombre of posiblesNombres) {
    if (Array.isArray(persona[nombre]) && persona[nombre].length > 0) return persona[nombre];
  }
  return [];
}
function categoriasDe(p) {
  if (Array.isArray(p.categorias_discapacidades) && p.categorias_discapacidades.length) return p.categorias_discapacidades;
  return getAsoc(p, "CategoriaDiscapacidads", "CategoriaDiscapacidades", "CategoriaDiscapacidad", "categoriaDiscapacidads");
}

// Mismo criterio que el backend: solo quien creó el registro, o un admin,
// puede eliminarlo. Registros viejos sin creado_por_id solo los puede
// tocar un admin.
function puedeEliminar(persona, user) {
  if (user?.rol === "admin") return true;
  if (persona._local) return true; // pendiente sin sincronizar todavía — nadie más pudo haberlo creado
  return persona.creado_por_id != null && persona.creado_por_id === user?.id;
}

export default function TablaDiscapacidad() {
  const online = useOnlineStatus();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [loadingPdf,  setLoadingPdf]  = useState(null);
  const [lista,       setLista]       = useState([]);
  const [showModal,   setShowModal]   = useState(false);
  const [personaEdit, setPersonaEdit] = useState(null);
  const [showConsentimiento, setShowConsentimiento] = useState(false);
  const [personaConsentimiento, setPersonaConsentimiento] = useState(null);
  const [busqueda,    setBusqueda]    = useState("");
  const [territorioFiltro, setTerritorioFiltro] = useState("");
  const [comunidadFiltro, setComunidadFiltro] = useState("");
  const [pagina,      setPagina]      = useState(1);
  const [modoOffline, setModoOffline] = useState(!navigator.onLine);
  const [cargando,    setCargando]    = useState(true);
  const [actualizadoEn, setActualizadoEn] = useState(null);
  const [vista,       setVista]       = useState("tabla"); // "tabla" | "mapa"
  const [personaFoco, setPersonaFoco] = useState(null);

  const cargandoRef = useRef(false);

  // Solo los territorios/comunidades que YA tienen algún registro — no el
  // catálogo completo. Se recalculan solos cada vez que cambia `lista`.
  const territoriosConDatos = useMemo(() => {
    return Array.from(new Set(lista.map(p => p.municipio).filter(Boolean))).sort();
  }, [lista]);

  const comunidadesConDatos = useMemo(() => {
    const base = territorioFiltro ? lista.filter(p => p.municipio === territorioFiltro) : lista;
    return Array.from(new Set(base.map(p => p.nombre_comunidad).filter(Boolean))).sort();
  }, [lista, territorioFiltro]);

  // ── PDF ───────────────────────────────────────────────────────────────────
  // 1) Se intenta el PDF generado en el servidor (descarga directa).
  // 2) Si el servidor no puede (hosting sin Chrome → responde 503
  //    PDF_EN_NAVEGADOR), se trae la misma ficha en HTML y se abre el diálogo
  //    de impresión del navegador sobre un iframe oculto: el usuario elige
  //    "Guardar como PDF". Mismo diseño, sin cargar el servidor.
  const imprimirFichaEnNavegador = (html) =>
    new Promise((resolve) => {
      const iframe = document.createElement("iframe");
      Object.assign(iframe.style, { position: "fixed", right: 0, bottom: 0, width: 0, height: 0, border: 0 });
      iframe.setAttribute("aria-hidden", "true");
      document.body.appendChild(iframe);
      iframe.onload = () => {
        const w = iframe.contentWindow;
        // Pequeña espera para que carguen los logos (data URI) antes de imprimir.
        setTimeout(() => {
          w.focus();
          w.print();
          // Se retira el iframe cuando se cierra el diálogo de impresión.
          setTimeout(() => { iframe.remove(); resolve(); }, 1000);
        }, 300);
      };
      iframe.srcdoc = html;
    });

  const handleDescargarPdf = async (id, documento) => {
    try {
      setLoadingPdf(id);
      try {
        const res  = await descargarPdfRBC(id);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url  = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `RBC-Discapacidad-${documento || id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return;
      } catch (errorPdf) {
        // Cualquier respuesta del servidor que no sea el PDF (503 sin Chrome,
        // 500…) cae al plan B. Sin conexión (sin respuesta) sí es un error real.
        if (!errorPdf?.response) throw errorPdf;
      }

      const { data: html } = await obtenerFichaHtmlRBC(id);
      await imprimirFichaEnNavegador(html);
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "No se pudo generar la ficha",
        text: error?.response ? "Inténtalo de nuevo en unos segundos." : "La ficha necesita conexión a internet.",
        confirmButtonColor: "#1a3a5c",
      });
    } finally {
      setLoadingPdf(null);
    }
  };

  // ── CARGAR DATOS ──────────────────────────────────────────────────────────
  const loadData = async (silencioso = false) => {
    if (cargandoRef.current) return;
    cargandoRef.current = true;
    if (!silencioso) setCargando(true);

    if (!navigator.onLine) {
      setModoOffline(true);
      try {
        const cache = await db.discapacidad_cache.toArray();
        const pendientes = await db.discapacidad_pendientes.filter(r => r.sincronizado === 0).toArray();

        // Los que tienen una eliminación pendiente sin sincronizar todavía
        // no deben seguir apareciendo en la lista — si no, parece que
        // "no se eliminó" aunque la eliminación ya está en cola.
        const idsConEliminacionPendiente = new Set(
          pendientes.filter(r => r._tipo === "eliminar").map(r => r._editId)
        );
        const cacheVisible = cache.filter(c => !idsConEliminacionPendiente.has(c.id));

        const pendientesVisibles = pendientes
          .filter(r => r._tipo !== "eliminar")
          .map(r => ({
            ...r,
            id: `local_${r.id}`,
            categorias_discapacidades: (r.categorias_discapacidad || []).map(d => ({ descripcion: d })),
            _local: true,
          }));

        setLista([...cacheVisible, ...pendientesVisibles]);
      } catch (e) {
        console.error("❌ Error offline:", e);
      } finally {
        setCargando(false);
        cargandoRef.current = false;
      }
      return;
    }

    try {
      const res = await getPersonas();
      setModoOffline(false);
      setLista(res.data);
      setActualizadoEn(new Date());

      // Transacción atómica: si bulkAdd fallara a medio camino (ej. un
      // registro con datos raros), el clear() se revierte solo y la caché
      // vieja queda intacta — antes, si bulkAdd tronaba después del
      // clear(), la caché se quedaba VACÍA para siempre (perdías el
      // acceso offline a todo lo que ya tenías guardado).
      const serializado = JSON.parse(JSON.stringify(res.data));
      await db.transaction("rw", db.discapacidad_cache, async () => {
        await db.discapacidad_cache.clear();
        await db.discapacidad_cache.bulkAdd(serializado);
      });
    } catch (error) {
      console.error("❌ Error cargando registros:", error);
      const esErrorDeRed = !error.response;

      if (error.response?.status === 401) {
        Swal.fire({
          icon: "warning", title: "Tu sesión expiró", text: "Vuelve a iniciar sesión para continuar.",
          confirmButtonColor: "#1a3a5c",
        }).then(() => { logout(); navigate("/login"); });
        return;
      }

      if (esErrorDeRed) {
        setModoOffline(true);
        try {
          const cache = await db.discapacidad_cache.toArray();
          if (cache.length > 0) setLista(cache);
        } catch (e) { console.error(e); }
      } else if (!silencioso) {
        Swal.fire({
          icon: "error", title: "No se pudieron cargar los registros",
          text: error.response?.data?.message || "Intenta de nuevo en unos segundos.",
          confirmButtonColor: "#1a3a5c",
        });
      }
    } finally {
      setCargando(false);
      cargandoRef.current = false;
    }
  };

  // ── ELIMINAR (con soporte offline) ──────────────────────────────────────────
  const eliminar = async (id, nombre) => {
    const confirmacion = await Swal.fire({
      icon: "warning", title: "¿Eliminar este registro?", text: nombre,
      showCancelButton: true, confirmButtonText: "Sí, eliminar", cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });
    if (!confirmacion.isConfirmed) return;

    const estaOnline = online && navigator.onLine;

    if (!estaOnline) {
      try {
        await eliminarOfflineDiscapacidad(id);
        await Swal.fire({ icon: "info", title: "📴 Se eliminará al reconectar", timer: 1500, showConfirmButton: false });
        loadData();
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "No se pudo guardar la eliminación", confirmButtonColor: "#1a3a5c" });
      }
      return;
    }

    try {
      await deletePersona(id);
      loadData();
    } catch (error) {
      console.error(error);
      // "online" decía que sí, pero la petición falló por red de verdad
      // (no fue el servidor rechazándola) — se cae a la cola offline en
      // vez de perder la acción del usuario.
      if (!error.response) {
        try {
          await eliminarOfflineDiscapacidad(id);
          await Swal.fire({ icon: "info", title: "📴 Sin conexión — se eliminará al reconectar", timer: 1800, showConfirmButton: false });
          loadData();
          return;
        } catch (e2) { console.error(e2); }
      }
      Swal.fire({ icon: "error", title: "No se pudo eliminar", text: error.response?.data?.message || "", confirmButtonColor: "#1a3a5c" });
    }
  };

  // ── EXCEL ─────────────────────────────────────────────────────────────────
  const descargarExcel = async () => {
    try {
      const res = await exportarExcelRBC();
      let filename = "RBC_Discapacidad.xlsx";
      const disposition = res.headers["content-disposition"];
      if (disposition?.includes("filename=")) filename = disposition.split("filename=")[1].replace(/"/g, "");
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click();
      link.remove(); window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: "error", title: "No se pudo exportar", confirmButtonColor: "#1a3a5c" });
    }
  };

  // ── FILTRO + PAGINACIÓN ───────────────────────────────────────────────────
  const listaFiltrada = lista.filter(p => {
    const coincideTexto =
      p.primer_nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
      p.documento?.toLowerCase().includes(busqueda.toLowerCase());
    const coincideTerritorio = !territorioFiltro || p.municipio === territorioFiltro;
    const coincideComunidad = !comunidadFiltro || p.nombre_comunidad === comunidadFiltro;
    return coincideTexto && coincideTerritorio && coincideComunidad;
  });

  const totalPags  = Math.ceil(listaFiltrada.length / REGISTROS_POR_PAGINA) || 1;
  const paginados  = listaFiltrada.slice((pagina - 1) * REGISTROS_POR_PAGINA, pagina * REGISTROS_POR_PAGINA);

  // ── EFECTOS ───────────────────────────────────────────────────────────────
  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setModoOffline(!online); }, [online]);
  useEffect(() => {
    if (!online) return;
    sincronizarDiscapacidadPendientes().then(() => loadData());
  }, [online]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onSync = () => loadData();
    window.addEventListener("sync-completado", onSync);
    return () => window.removeEventListener("sync-completado", onSync);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!online) return;
    const intervalo = setInterval(() => loadData(true), INTERVALO_POLLING_MS);
    return () => clearInterval(intervalo);
  }, [online]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible" && online) loadData(true); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [online]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPagina(1); }, [busqueda, territorioFiltro, comunidadFiltro]);
  useEffect(() => { setComunidadFiltro(""); }, [territorioFiltro]);

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }} className="py-3">

      {/* Header degradado */}
      <div style={{
        background: "linear-gradient(135deg, #1a3a5c 0%, #2d5986 60%, #3a6ea5 100%)", borderLeft: `6px solid ${ESTRATEGIA_COLOR.rbc}`, boxShadow: "0 10px 26px rgba(26,58,92,.18)",
        borderRadius: 16, padding: "20px 24px", marginBottom: 20,
        color: "#fff", position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", right: -30, top: -30, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
        <div style={{ fontSize: 11, opacity: 0.75, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 3 }}>
          ♿ Rehabilitación Basada en Comunidad · 2026
        </div>
        <h5 style={{ margin: 0, fontWeight: 800, fontSize: "1.15rem" }}>Discapacidad — RBC</h5>
        <div style={{ marginTop: 5, fontSize: 13, opacity: 0.85 }}>
          Secretaría de Salud del Amazonas
          {actualizadoEn && !modoOffline && (
            <> · actualizado {actualizadoEn.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}</>
          )}
        </div>
      </div>

      {modoOffline && (
        <div className="alert alert-warning py-2 px-3 mb-3 d-flex align-items-center gap-2" style={{ borderRadius: 10, fontSize: "0.85rem" }}>
          <span>📴</span>
          <div><strong>Modo sin conexión</strong> — {lista.length} registro(s) local(es). Se sincronizarán al reconectarse.</div>
        </div>
      )}

      {/* Barra de acciones */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <Form.Control
          placeholder="🔎 Buscar nombre o documento..."
          value={busqueda} onChange={e => setBusqueda(e.target.value)}
          style={{ maxWidth: 260, fontSize: 13 }}
        />

        <Form.Select
          value={territorioFiltro}
          onChange={e => setTerritorioFiltro(e.target.value)}
          style={{ maxWidth: 220, fontSize: 13 }}
        >
          <option value="">Todos los territorios</option>
          {territoriosConDatos.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </Form.Select>

        <Form.Select
          value={comunidadFiltro}
          onChange={e => setComunidadFiltro(e.target.value)}
          style={{ maxWidth: 220, fontSize: 13 }}
        >
          <option value="">Todas las comunidades</option>
          {comunidadesConDatos.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </Form.Select>

        <Button
          variant={vista === "mapa" ? "primary" : "outline-primary"}
          onClick={() => { setVista(v => v === "mapa" ? "tabla" : "mapa"); setPersonaFoco(null); }}
          style={vista === "mapa" ? { background: AZUL, border: "none", fontSize: 13 } : { fontSize: 13 }}
        >
          {vista === "mapa" ? "📋 Ver tabla" : "🗺️ Ver mapa"}
        </Button>

        <Dropdown as={ButtonGroup}>
          <Dropdown.Toggle variant="outline-success" style={{ fontSize: 13, fontWeight: 600 }}>
            📥 Excel
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={descargarExcel}>Descargar todo</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <div style={{ marginLeft: "auto" }}>
          <Button
            onClick={() => { setPersonaEdit(null); setShowModal(true); }}
            style={{
              background: "linear-gradient(135deg, #1a3a5c, #2d5986)",
              border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, padding: "8px 18px",
            }}
          >
            ➕ Nuevo registro
          </Button>
        </div>
      </div>

      {/* Mapa o tabla */}
      {vista === "mapa" ? (
        <MapaDiscapacidad registros={listaFiltrada} foco={personaFoco} />
      ) : cargando ? (
        <div style={{ textAlign: "center", padding: 60, color: "#6c757d" }}>
          <div style={{ fontSize: "2rem", marginBottom: 8 }}>⏳</div>Cargando registros...
        </div>
      ) : listaFiltrada.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", background: "#fff", borderRadius: 16, border: "2px dashed #dee2e6" }}>
          <div style={{ fontSize: "3rem", marginBottom: 10 }}>♿</div>
          <div style={{ fontWeight: 600, color: "#495057" }}>
            {lista.length === 0 ? "Sin registros todavía" : "Sin resultados para este filtro"}
          </div>
          {lista.length === 0 && (
            <div style={{ color: "#adb5bd", fontSize: 13, marginTop: 4 }}>Usa "Nuevo registro" para empezar</div>
          )}
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,.06)" }}>
          <div style={{ overflowX: "auto" }}>
            <Table hover style={{ margin: 0, fontSize: 13, minWidth: 900 }}>
              <thead>
                <tr style={{ background: AZUL, color: "#fff" }}>
                  <th style={TH}>#</th>
                  <th style={{ ...TH, minWidth: 190 }}>Persona</th>
                  <th style={TH}>Documento</th>
                  <th style={TH}>Edad</th>
                  <th style={TH}>Sexo</th>
                  <th style={{ ...TH, minWidth: 180 }}>Discapacidad</th>
                  <th style={{ ...TH, textAlign: "center" }}>Estado</th>
                  <th style={{ ...TH, textAlign: "center" }} title="Consentimiento informado">Doc.</th>
                  <th style={{ ...TH, textAlign: "center" }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {paginados.map((p, i) => (
                  <tr key={p.id} style={{ borderBottom: "1px solid #f1f3f5" }}>
                    <td style={{ ...TD, color: "#adb5bd", fontWeight: 600 }}>{(pagina - 1) * REGISTROS_POR_PAGINA + i + 1}</td>
                    <td style={TD}>
                      <div style={{ fontWeight: 600, color: AZUL, display: "flex", alignItems: "center", gap: 6 }}>
                        {`${p.primer_nombre || ""} ${p.primer_apellido || ""}`}
                        {p._local && (
                          <span title="Pendiente de sincronizar" style={{ background: "#FAEEDA", color: "#854F0B", border: "1px solid #EF9F27", borderRadius: 20, padding: "1px 7px", fontSize: 9, fontWeight: 700 }}>⏳</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "#6b7280" }}>{p.municipio || "—"}</div>
                    </td>
                    <td style={TD}>
                      <span style={{ background: "#f1f5f9", borderRadius: 5, padding: "2px 7px", fontFamily: "monospace", fontSize: 12 }}>
                        {p.documento}
                      </span>
                    </td>
                    <td style={TD}>
                      {[p.edad_anios ? `${p.edad_anios}a` : null, p.edad_meses ? `${p.edad_meses}m` : null].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td style={TD}>
                      <span style={{
                        fontSize: "0.72rem", fontWeight: 700, padding: "2px 9px", borderRadius: 20,
                        background: p.sexo === "Masculino" ? "#e8f0fb" : p.sexo === "Femenino" ? "#fce8f1" : "#f1f5f9",
                        color:      p.sexo === "Masculino" ? "#2e5a8c" : p.sexo === "Femenino" ? "#b5457a" : "#6c757d",
                      }}>
                        {p.sexo || "—"}
                      </span>
                    </td>
                    <td style={{ ...TD, fontSize: 12 }}>{categoriasDe(p).map(d => d.descripcion).join(", ") || "—"}</td>
                    <td style={{ ...TD, textAlign: "center" }}>
                      {p._local
                        ? <Badge bg="warning" text="dark">⏳</Badge>
                        : <Badge bg="success">✅</Badge>}
                    </td>
                    <td style={{ ...TD, textAlign: "center" }}>
                      <span
                        title={p.consentimiento_archivo_url ? `Consentimiento: ${p.consentimiento_archivo_nombre || "cargado"}` : "Sin consentimiento informado"}
                        style={{ fontSize: 13, color: p.consentimiento_archivo_url ? "#198754" : "#adb5bd" }}
                      >
                        ✍️{p.consentimiento_archivo_url ? "✅" : "❌"}
                      </span>
                    </td>
                    <td style={{ ...TD, textAlign: "center", whiteSpace: "nowrap" }}>
                      <button style={BTN_ICONO} title="Editar" onClick={() => { setPersonaEdit(p); setShowModal(true); }}>✏️</button>
                      <button
                        style={BTN_ICONO}
                        title={p.consentimiento_archivo_url ? "Ver / reemplazar consentimiento" : "Cargar consentimiento"}
                        onClick={() => { setPersonaConsentimiento(p); setShowConsentimiento(true); }}
                        disabled={p._local}
                      >
                        ✍️
                      </button>
                      {p.latitud && p.longitud && (
                        <button style={BTN_ICONO} title="Ver en mapa" onClick={() => { setPersonaFoco(p); setVista("mapa"); }}>🗺️</button>
                      )}
                      {!p._local && !modoOffline && (
                        <button style={BTN_ICONO} title="Descargar ficha PDF" disabled={loadingPdf === p.id}
                          onClick={() => handleDescargarPdf(p.id, p.documento)}>
                          {loadingPdf === p.id ? "⏳" : "🖨️"}
                        </button>
                      )}
                      {puedeEliminar(p, user) && (
                        <button style={BTN_ICONO} title="Eliminar" onClick={() => eliminar(p.id, `${p.primer_nombre} ${p.primer_apellido}`)}>🗑️</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Footer + paginación */}
          <div style={{
            padding: "10px 20px", background: "#f8faf8", borderTop: "1px solid #e9ecef",
            fontSize: 12, color: "#6c757d", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8,
          }}>
            <span>
              Mostrando <strong>{paginados.length}</strong> de <strong>{listaFiltrada.length}</strong> · Total: <strong>{lista.length}</strong>
            </span>
            {totalPags > 1 && (
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}
                  style={{ border: "1px solid #dee2e6", background: pagina === 1 ? "#f8f9fa" : "#fff", borderRadius: 6, padding: "3px 10px", cursor: pagina === 1 ? "not-allowed" : "pointer", fontSize: 12 }}>◀</button>
                <span style={{ fontWeight: 700 }}>{pagina}/{totalPags}</span>
                <button onClick={() => setPagina(p => Math.min(totalPags, p + 1))} disabled={pagina === totalPags}
                  style={{ border: "1px solid #dee2e6", background: pagina === totalPags ? "#f8f9fa" : "#fff", borderRadius: 6, padding: "3px 10px", cursor: pagina === totalPags ? "not-allowed" : "pointer", fontSize: 12 }}>▶</button>
              </div>
            )}
          </div>
        </div>
      )}

      <DiscapacidadModal
        show={showModal}
        onClose={() => { setShowModal(false); setPersonaEdit(null); }}
        reload={loadData}
        persona={personaEdit}
      />

      <ModalConsentimiento
        show={showConsentimiento}
        onClose={() => { setShowConsentimiento(false); setPersonaConsentimiento(null); }}
        persona={personaConsentimiento}
        reload={loadData}
      />
    </div>
  );
}